import type {
  DataScope,
  KnownPermissionName,
  KnownRoleName,
  PermissionScopeLevel,
} from '@/types/db'

export const PERMISSIONS = {
  SALES_CHANNEL_CONFIG: 'sales_channel:config',
  SALES_CHANNEL_VIEW: 'sales_channel:view',
  SALES_CHANNEL_MAP_LISTING: 'sales_channel:map_listing',
  PRODUCT_KEYWORD_MANAGE: 'product:keyword_manage',
  PAYMENT_GATEWAY_CONFIG: 'payment_gateway:config',
  // Order permissions — đã seed sẵn ở migration 20260520_0001_init_auth.sql.
  // Khi user mở rộng scope (assigned/department/own_created), thêm constants
  // tại đây và mirror vào features/orders/orders-repository.ts.
  ORDER_VIEW: 'order:view',
  ORDER_EDIT: 'order:edit',
  // Giao đơn cho NGƯỜI KHÁC (ô "Sale phụ trách" của form đơn) — migration
  // 20260922_0006. Có 4 biến thể scope-in-name; gán cho CHÍNH MÌNH luôn được
  // nên đây chỉ là cổng cho việc gán người khác. Enforce ở
  // create_order_with_items / update_order_with_items (RLS không phân biệt
  // được ý định "có đổi người phụ trách hay không").
  ORDER_ASSIGN: 'order:assign',
  // Kế toán nhập/sửa số hoá đơn (file 05). Tách khỏi order:edit để kế toán
  // không cần quyền sửa đơn đầy đủ.
  ORDER_INVOICE_EDIT: 'order:invoice_edit',
  // Sửa thông tin xuất hóa đơn của ĐƠN (snapshot) — cho phép sửa kể cả khi đơn
  // đã đẩy VC / đối soát. Tách khỏi order:invoice_edit (số HĐ) và order:edit.
  ORDER_INVOICE_INFO_EDIT: 'order:invoice_info_edit',
  // Sửa NGUỒN của đơn — cho phép sửa kể cả khi đơn đã đẩy VC / đối soát (qua RPC
  // set_order_source). Tách khỏi order:edit để không cần quyền sửa đơn đầy đủ.
  ORDER_SOURCE_EDIT: 'order:source_edit',
  // Sửa SALE PHỤ TRÁCH của đơn kể cả khi đã đẩy VC / đối soát (qua RPC
  // set_order_assignee). Chỉ mở KHÓA "đơn đã chốt"; GIAO CHO AI vẫn do
  // order:assign + scope quyết định (RPC kiểm cả hai).
  ORDER_ASSIGNEE_EDIT: 'order:assignee_edit',
  // Ghi chú đơn (note chung) KHÔNG có quyền riêng — dùng order:edit/order:create.
  // 3 ghi chú bộ phận tách quyền riêng; thiếu quyền → chỉ xem mục đó.
  ORDER_NOTE_DESIGN: 'order:note_design',
  ORDER_NOTE_WORKSHOP: 'order:note_workshop',
  ORDER_NOTE_SHIPPING: 'order:note_shipping',
  ORDER_NOTE_FILE: 'order:note_file',
  // Vận chuyển / in / đối soát đơn (catalog 20260622) — backend gate ở Phase 3.
  ORDER_CREATE: 'order:create',
  ORDER_CANCEL: 'order:cancel',
  // Hoàn đơn (chuyển sang 'cancelled_return' = Huỷ khách trả hàng) — tách khỏi
  // order:cancel; gate riêng ở change_order_status + nút hoàn FE.
  ORDER_REFUND: 'order:refund',
  ORDER_STATUS: 'order:status',
  // Sửa trạng thái đơn ĐÃ CHỐT (đã đối soát COD / đã huỷ) — gate thêm ngoài
  // order:status ở change_order_status + set_manual_shipment_status (override).
  ORDER_STATUS_OVERRIDE: 'order:status_override',
  ORDER_TAG: 'order:tag',
  ORDER_EXPORT: 'order:export',
  ORDER_PUSH_SHIPPING: 'order:push_shipping',
  ORDER_PRINT: 'order:print',
  ORDER_RECONCILE: 'order:reconcile',
  ORDER_LOCK_OVERRIDE: 'order:lock_override',
  CUSTOMER_VIEW: 'customer:view',
  CUSTOMER_EDIT: 'customer:edit',
  CUSTOMER_CREATE: 'customer:create',
  CUSTOMER_DELETE: 'customer:delete',
  // Tài chính (catalog 20260622) — backfill THỦ CÔNG cho vị trí Kế toán.
  FINANCE_VIEW: 'finance:view',
  FINANCE_PAYMENT_CREATE: 'finance:payment_create',
  FINANCE_PAYMENT_DELETE: 'finance:payment_delete',
  FINANCE_DEBT_VIEW: 'finance:debt_view',
  // Sản phẩm & vật tư granular (catalog 20260622). MATERIAL_COST_EDIT tách giá vốn.
  PRODUCT_VIEW: 'product:view',
  PRODUCT_CREATE: 'product:create',
  PRODUCT_EDIT: 'product:edit',
  PRODUCT_DELETE: 'product:delete',
  MATERIAL_VIEW: 'material:view',
  MATERIAL_CREATE: 'material:create',
  MATERIAL_EDIT: 'material:edit',
  MATERIAL_DELETE: 'material:delete',
  MATERIAL_COST_EDIT: 'material:cost_edit',
  // Vật tư kho (wh_materials, migration 20260623_0001) — tách khỏi material:*.
  WH_MATERIAL_VIEW: 'wh_material:view',
  WH_MATERIAL_CREATE: 'wh_material:create',
  WH_MATERIAL_EDIT: 'wh_material:edit',
  WH_MATERIAL_DELETE: 'wh_material:delete',
  // Sửa/xoá LỊCH SỬ NHẬP vật tư (phiếu PN…) — tách khỏi wh_material:edit (nhạy cảm:
  // ảnh hưởng tồn + định giá). Enforce ở RPC wh_stock_in_line_edit/receipt_* (topic 02).
  WH_MATERIAL_STOCK_EDIT: 'wh_material:stock_edit',
  WH_MATERIAL_STOCK_DELETE: 'wh_material:stock_delete',
  // Bán thành phẩm (wh_btp_items, migration 20260920_0001) — khung / khung xi /
  // thành phẩm SX sẵn. scope_level = null (tồn của xưởng, không có phạm vi theo người).
  BTP_VIEW: 'btp:view',
  BTP_ITEM_MANAGE: 'btp:item_manage',
  BTP_STOCK_IN: 'btp:stock_in',
  BTP_PRODUCE: 'btp:produce',
  BTP_ADJUST: 'btp:adjust',
  // Xuất kho tay (không gắn đơn) — TÁCH khỏi btp:adjust: điều chỉnh là sửa
  // sai lệch kiểm kê, còn đây là hàng thật rời kho.
  BTP_ISSUE_MANUAL: 'btp:issue_manual',
  // Xác nhận / huỷ phiếu kiểm kê BTP — cùng là UPDATE trên wh_btp_checklists nên
  // RLS không phân biệt được intent → gate inline trong RPC wh_btp_confirm/cancel.
  BTP_CHECKLIST_CONFIRM: 'btp:checklist_confirm',
  BTP_CHECKLIST_CANCEL: 'btp:checklist_cancel',
  // Lập/sửa/huỷ PHIẾU sản xuất thành phẩm làm sẵn (migration 20260923_0001).
  // Tách khỏi btp:produce vì quyền đó nghĩa là "được tiêu vật tư kho chính";
  // người lập kế hoạch không cần quyền tiêu kho. ĐỌC vẫn dùng btp:view.
  BTP_PREBUILT_MANAGE: 'btp:prebuilt_manage',
  // Nhập hàng & NCC granular (catalog 20260622).
  PURCHASE_VIEW: 'purchase:view',
  PURCHASE_CREATE: 'purchase:create',
  PURCHASE_EDIT: 'purchase:edit',
  PURCHASE_DELETE: 'purchase:delete',
  PURCHASE_RECEIVE: 'purchase:receive',
  PURCHASE_STOCK: 'purchase:stock',
  SUPPLIER_VIEW: 'supplier:view',
  SUPPLIER_CREATE: 'supplier:create',
  SUPPLIER_EDIT: 'supplier:edit',
  SUPPLIER_DELETE: 'supplier:delete',
  USER_MANAGE: 'user:manage',
  USER_CREATE: 'user:create',
  USER_RESET_PASSWORD: 'user:reset_password',
  USER_DEACTIVATE: 'user:deactivate',
  USER_DELETE: 'user:delete',
  ROLE_MANAGE: 'role:manage',
  FRAME_COLOR_MANAGE: 'frame_color:manage',
  TAG_MANAGE: 'tag:manage',
  SOURCE_MANAGE: 'source:manage',
  SYSTEM_CONFIG: 'system:config',
  DEPARTMENT_MANAGE: 'department:manage',
  POSITION_MANAGE: 'position:manage',
  BRANCH_MANAGE: 'branch:manage',
  SHIPPING_CONFIG_API_KEY: 'shipping:config_api_key',
  // Đổi trạng thái giao TAY cho Ship ngoài (provider='manual') — migration
  // 20260606_0003. Giao thành công → đối soát → ghi doanh thu.
  SHIPPING_MANUAL_STATUS: 'shipping:manual_status',
  // Reports module (migration 20260603_0001) — 4 quyền theo scope dữ liệu.
  // Gallery + builder hiển thị nếu user có BẤT KỲ quyền nào trong nhóm này.
  REPORT_DASHBOARD: 'report:dashboard',
  REPORT_REVENUE: 'report:revenue',
  REPORT_PROFIT: 'report:profit',
  REPORT_BY_USER: 'report:by_user',
  REPORT_EXPORT: 'report:export',
  // Giám sát hiệu suất Sale từ Pancake (migration 20260609_0001).
  PANCAKE_VIEW: 'pancake:view',
  PANCAKE_MANAGE: 'pancake:manage',
  // Duyệt chi (module đề nghị thanh toán — catalog 20260713_0002).
  EXPENSE_REQUEST: 'expense:request',
  EXPENSE_VIEW: 'expense:view',
  EXPENSE_APPROVE: 'expense:approve',
  // Xem + chi phiếu theo TỪNG nguồn tiền (migration 20261005_0002/0003). Thay hẳn quyền
  // expense:disburse cũ (bỏ ở 20261006_0001): giữ quỹ nguồn nào thì chi phiếu nguồn đó.
  EXPENSE_FUND_WAREHOUSE: 'expense:fund_warehouse',
  EXPENSE_FUND_SHIPPING: 'expense:fund_shipping',
  EXPENSE_FUND_TREASURER: 'expense:fund_treasurer',
  EXPENSE_FUND_COMPANY: 'expense:fund_company',
  EXPENSE_FUND_CASH: 'expense:fund_cash',
  EXPENSE_FLOW_MANAGE: 'expense:flow_manage',
  EXPENSE_TYPE_MANAGE: 'expense:type_manage',
  EXPENSE_REPORT_VIEW: 'expense:report_view',
  EXPENSE_REPORT_EXPORT: 'expense:report_export',
  // Xuất Excel danh sách phiếu theo bộ lọc ở tab Phiếu chi (migration 20261104_0001). Tách khỏi
  // EXPENSE_REPORT_EXPORT vì file này có cả phiếu chưa chi.
  EXPENSE_EXPORT: 'expense:export',
  EXPENSE_CREATE_COMMAND: 'expense:create_command',
  // Thẻ "Đã chuyển tiền": gắn / sửa / gỡ trên mọi phiếu XEM được (migration 20261023_0001).
  EXPENSE_TRANSFER_TAG: 'expense:transfer_tag',
  DEPOSIT_CREATE: 'deposit:create',
  DEPOSIT_VIEW: 'deposit:view',
  DEPOSIT_VIEW_OWN: 'deposit:view:own',
  DEPOSIT_VIEW_SUBORDINATES: 'deposit:view:subordinates',
  DEPOSIT_VIEW_BRANCH: 'deposit:view:branch',
  DEPOSIT_VIEW_ALL: 'deposit:view:all',
  DEPOSIT_CONFIRM: 'deposit:confirm',
  // Hạ COD (migration 20261026_0002). Sale phụ trách / người duyệt theo vị trí
  // KHÔNG cần quyền module — DB nhận họ theo người/vị trí của bước. view có 4
  // biến thể scope-in-name, phạm vi áp cho người đề nghị HOẶC sale phụ trách.
  // KHÔNG có quyền "xác nhận đã hạ": bước đó luôn do chính người tạo đề nghị làm.
  COD_REDUCTION_REQUEST: 'cod_reduction:request',
  COD_REDUCTION_VIEW: 'cod_reduction:view',
  COD_REDUCTION_APPROVE: 'cod_reduction:approve',
  COD_REDUCTION_FLOW_MANAGE: 'cod_reduction:flow_manage',
  COD_REDUCTION_REASON_MANAGE: 'cod_reduction:reason_manage',
  COD_REDUCTION_REPORT_VIEW: 'cod_reduction:report_view',
  COD_REDUCTION_REPORT_EXPORT: 'cod_reduction:report_export',
  // Khách tiềm năng (lead) — port từ aloha-dms, migration 20260905_0001.
  // lead:view/edit có scope-in-name; các quyền hành động còn lại là on/off.
  LEAD_VIEW: 'lead:view',
  LEAD_VIEW_OWN: 'lead:view:own',
  LEAD_VIEW_SUBORDINATES: 'lead:view:subordinates',
  LEAD_VIEW_BRANCH: 'lead:view:branch',
  LEAD_VIEW_ALL: 'lead:view:all',
  // Kho lead đã thu hồi (idle + chưa ai phụ trách) — thấy được kể cả scope 'own'.
  LEAD_VIEW_IDLE: 'lead:view_idle',
  LEAD_CREATE: 'lead:create',
  LEAD_EDIT: 'lead:edit',
  LEAD_DELETE: 'lead:delete',
  LEAD_ASSIGN: 'lead:assign',
  LEAD_STATUS: 'lead:status',
  LEAD_DEAL_STATUS_EDIT: 'lead:deal_status_edit',
  LEAD_NOTE: 'lead:note',
  LEAD_EXPORT: 'lead:export',
  LEAD_IMPORT: 'lead:import',
  LEAD_CONVERT: 'lead:convert',
  LEAD_SETTING_MANAGE: 'lead_setting:manage',
  CUSTOMER_SOURCE_MANAGE: 'customer_source:manage',
  LEAD_CUSTOMER_TYPE_MANAGE: 'lead_customer_type:manage',
  KPI_VIEW: 'kpi:view',
  KPI_MANAGE: 'kpi:manage',
  REPORT_LEAD_USER: 'report:lead_user',
  REPORT_KPI_USER: 'report:kpi_user',
  REPORT_KPI_DEPARTMENT: 'report:kpi_department',
  REPORT_PRODUCTION: 'report:production',
  REPORT_PRODUCT_SIZE: 'report:product_size',
  // Báo cáo đơn sản xuất chậm (migration 20261030_0001) — TRẦN, xem toàn công ty.
  REPORT_SLOW_PRODUCTION: 'report:slow_production',
  // Khoán — tiền công theo đầu việc (migration 20261102_0001..0003, spec
  // module_khoan.md). approve có 3 biến thể scope-in-name (cấp dưới / chi nhánh /
  // mọi thợ). report:piecework CÓ tiền công → quyền riêng, không gộp report:production.
  PIECEWORK_DECLARE: 'piecework:declare',
  PIECEWORK_APPROVE: 'piecework:approve',
  PIECEWORK_CONFIG: 'piecework:config',
  PIECEWORK_CLOSE_PERIOD: 'piecework:close_period',
  REPORT_PIECEWORK: 'report:piecework',
} as const satisfies Record<string, KnownPermissionName>

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

// Mở menu "Báo cáo" (sidebar) + trang danh mục /reports: có BẤT KỲ quyền nào có
// thẻ trên trang đó. Sidebar và route PHẢI dùng chung danh sách này — trước đây 2
// nơi lệch nhau (route chỉ 4 quyền) và cùng thiếu quyền báo cáo xưởng. Thêm quyền
// report:* mới thì thêm vào đây (reports-hub-permissions.test.ts canh).
export const REPORTS_HUB_PERMISSIONS = [
  PERMISSIONS.REPORT_DASHBOARD,
  PERMISSIONS.REPORT_REVENUE,
  PERMISSIONS.REPORT_PROFIT,
  PERMISSIONS.REPORT_BY_USER,
  PERMISSIONS.REPORT_LEAD_USER,
  PERMISSIONS.REPORT_KPI_USER,
  PERMISSIONS.REPORT_KPI_DEPARTMENT,
  PERMISSIONS.REPORT_PRODUCTION,
  PERMISSIONS.REPORT_PRODUCT_SIZE,
  PERMISSIONS.REPORT_SLOW_PRODUCTION,
  PERMISSIONS.REPORT_PIECEWORK,
] as const

// ---------------------------------------------------------------------------
// Phạm vi dữ liệu (data scope) — migration 20260610_0001..0004.
// 4 cấp: 'all' (cả hệ thống) > 'department' (phòng ban) > 'subordinates'
// (mình + cấp dưới) > 'own' (bản thân). Mặc định DB = 'all'.
// ---------------------------------------------------------------------------
export const DATA_SCOPES = ['all', 'department', 'subordinates', 'own'] as const

// Các quyền CÓ scope ở giai đoạn 1 (order/customer). Quyền khác luôn 'all'.
export const SCOPED_PERMISSIONS = [
  'order:view',
  'order:edit',
  // Phạm vi GIAO ĐƠN cho ai (own/subordinates/branch/all) — migration
  // 20260922_0006. Danh sách trong ô "Sale phụ trách" do server lọc sẵn qua RPC
  // order_assignable_users; đừng quay lại đọc thẳng bảng profiles.
  'order:assign',
  'customer:view',
  'customer:edit',
  'expense:view',
  'deposit:view',
  'cod_reduction:view',
  'lead:view',
  'lead:edit',
  'kpi:view',
  // Phạm vi ĐẶT chỉ tiêu KPI (own/subordinates/branch/all) — migration
  // 20260908_0001. Trưởng nhóm nhận 'kpi:manage:subordinates' thì trang KPI chỉ
  // liệt kê nhân sự của mình (server lọc qua RPC kpi_manageable_users).
  'kpi:manage',
  // Phạm vi DUYỆT khoán (cấp dưới / chi nhánh / mọi thợ) — migration 20261102_0001.
  // Server lọc bằng can_access_record('piecework:approve', thợ) ở mọi RPC quản lý.
  'piecework:approve',
] as const satisfies readonly KnownPermissionName[]

export const SCOPE_LABELS: Record<DataScope, string> = {
  all: 'Toàn hệ thống',
  department: 'Phòng ban',
  subordinates: 'Mình + cấp dưới',
  own: 'Bản thân',
}

// Nhãn viết tắt cho badge ở permission-matrix.
export const SCOPE_SHORT_LABELS: Record<DataScope, string> = {
  all: 'HT',
  department: 'PB',
  subordinates: 'CD',
  own: 'BT',
}

// ---------------------------------------------------------------------------
// RBAC refactor (theo Nobi) — nhãn cho catalog scope-in-name (migration
// 20260622_0001). Dùng ở editor quyền kiểu Nobi.
// ---------------------------------------------------------------------------
export const PERMISSION_SCOPE_LABELS: Record<PermissionScopeLevel, string> = {
  own: 'Của mình',
  subordinates: 'Của cấp dưới',
  branch: 'Trong chi nhánh',
  all: 'Toàn bộ',
  source: 'Theo nguồn đơn',
}

// Thứ tự hẹp → rộng cho dropdown scope.
export const PERMISSION_SCOPE_ORDER: PermissionScopeLevel[] = [
  'own',
  'subordinates',
  'branch',
  'all',
  'source',
]

// Nhãn nhóm (resource) — tiêu đề khối collapsible trong tab module.
export const RESOURCE_LABELS: Record<string, string> = {
  order: 'Đơn hàng',
  customer: 'Khách hàng',
  report: 'Báo cáo',
  product: 'Sản phẩm',
  material: 'Vật tư',
  wh_material: 'Vật tư kho',
  btp: 'Bán thành phẩm',
  purchase: 'Đơn nhập hàng',
  supplier: 'Nhà cung cấp',
  finance: 'Thu chi & công nợ',
  sales_channel: 'Kênh bán',
  shipping: 'Vận chuyển',
  catalog: 'Danh mục',
  settings: 'Thiết lập',
  branch: 'Chi nhánh',
  pancake: 'Hiệu suất Sale',
  expense: 'Duyệt chi',
  deposit: 'Đặt cọc',
  cod_reduction: 'Hạ COD',
  lead: 'Khách tiềm năng',
  lead_setting: 'Cấu hình lead',
  kpi: 'KPI',
  piecework: 'Khoán',
}

// Nhãn hành động (action) — nhãn dòng quyền trong khối.
export const ACTION_LABELS: Record<string, string> = {
  view: 'Xem',
  create: 'Thêm',
  edit: 'Sửa',
  delete: 'Xoá',
  import: 'Import',
  export: 'Xuất Excel',
  print: 'In',
  status: 'Đổi trạng thái',
  cancel: 'Huỷ',
  confirm: 'Xác nhận về tiền',
  push: 'Đẩy vận chuyển',
  reconcile: 'Đối soát',
  invoice: 'Số hoá đơn',
  tag: 'Gắn thẻ',
  manage: 'Quản lý',
  config: 'Cấu hình',
  map: 'Map listing',
  revenue: 'Doanh thu',
  debt: 'Công nợ',
  cost: 'Giá vốn',
  receive: 'Nhận hàng',
  stock: 'Nhập kho',
  item_manage: 'Quản lý danh mục',
  stock_in: 'Nhập từ NCC',
  produce: 'Lệnh sản xuất',
  adjust: 'Điều chỉnh tồn',
  checklist_confirm: 'Xác nhận phiếu kiểm kê',
  checklist_cancel: 'Huỷ phiếu kiểm kê',
  keyword_manage: 'Quản lý từ khoá',
  manual_status: 'Cập nhật trạng thái giao',
  request: 'Tạo đề nghị',
  approve: 'Duyệt',
  disburse: 'Chi tiền',
  create_command: 'Tạo lệnh',
  flow_manage: 'Cấu hình luồng',
  type_manage: 'Quản lý loại TT',
  report_view: 'Xem báo cáo',
  report_export: 'Xuất báo cáo',
  assign: 'Phân bổ',
  note: 'Ghi điểm chạm',
  convert: 'Chuyển thành khách hàng',
  view_idle: 'Xem kho lead nhàn rỗi',
  lead_user: 'BC lead theo nhân viên',
  kpi_user: 'BC KPI theo nhân viên',
  kpi_department: 'BC KPI theo phòng ban',
  declare: 'Khai việc',
  close_period: 'Chốt kỳ',
  piecework: 'BC khoán',
}

// Nhãn mặc định cho các role hệ thống. Role do admin tạo qua UI sẽ fallback
// về `description` từ DB, hoặc về chính `name` nếu không có description.
export const ROLE_LABELS: Record<KnownRoleName, string> = {
  ceo: 'CEO',
  sales_manager: 'Trưởng phòng Sale',
  sale: 'Nhân viên sale',
  design: 'Đội Design',
  production: 'Đội Sản xuất',
  accountant: 'Kế toán',
  admin: 'Admin kỹ thuật',
  none: 'Chưa phân vai trò',
}

export const PERMISSION_LABELS: Record<KnownPermissionName, string> = {
  'sales_channel:config': 'Cấu hình kênh bán',
  'sales_channel:view': 'Xem kênh bán & logs',
  'sales_channel:map_listing': 'Map listing → sản phẩm',
  'product:keyword_manage': 'Quản lý từ khoá nhận diện',
  'payment_gateway:config': 'Cấu hình cổng thanh toán',
  'order:view': 'Xem đơn hàng',
  'order:edit': 'Chỉnh sửa đơn hàng',
  'order:assign': 'Giao đơn cho sale khác',
  'order:invoice_edit': 'Nhập/sửa số hoá đơn (kế toán)',
  'order:invoice_info_edit': 'Sửa thông tin của đơn',
  'order:source_edit': 'Sửa nguồn của đơn (kể cả đã đẩy VC)',
  'order:assignee_edit': 'Sửa sale phụ trách của đơn (kể cả đã đẩy VC)',
  'order:note_design': 'Sửa ghi chú design',
  'order:note_workshop': 'Sửa ghi chú xưởng',
  'order:note_shipping': 'Sửa ghi chú vận đơn',
  'order:note_file': 'Sửa ghi chú file',
  'order:create': 'Tạo đơn hàng',
  'order:cancel': 'Huỷ đơn hàng',
  'order:refund': 'Hoàn đơn (khách trả hàng)',
  'order:status': 'Đổi trạng thái đơn',
  'order:status_override': 'Sửa trạng thái đơn đã chốt',
  'order:tag': 'Gắn thẻ đơn hàng',
  'order:export': 'Xuất Excel đơn hàng',
  'order:push_shipping': 'Đẩy đơn sang hãng vận chuyển',
  'order:print': 'In đơn / hoá đơn',
  'order:reconcile': 'Đối soát đơn (COD)',
  'order:lock_override': 'Giành quyền sửa đơn (override khoá)',
  'customer:view': 'Xem khách hàng',
  'customer:edit': 'Chỉnh sửa khách hàng',
  'customer:create': 'Thêm khách hàng',
  'customer:delete': 'Xoá khách hàng',
  'finance:view': 'Xem thu chi & công nợ',
  'finance:payment_create': 'Tạo phiếu thu / chi',
  'finance:payment_delete': 'Xoá phiếu thu / chi',
  'finance:debt_view': 'Xem công nợ',
  'product:view': 'Xem sản phẩm',
  'product:create': 'Thêm sản phẩm',
  'product:edit': 'Sửa sản phẩm',
  'product:delete': 'Xoá sản phẩm',
  'material:view': 'Xem vật tư',
  'material:create': 'Thêm vật tư',
  'material:edit': 'Sửa vật tư',
  'material:delete': 'Xoá vật tư',
  'material:cost_edit': 'Nhập giá vốn vật tư',
  'wh_material:view': 'Xem vật tư kho',
  'wh_material:create': 'Thêm vật tư kho',
  'wh_material:edit': 'Sửa vật tư kho',
  'wh_material:delete': 'Xoá vật tư kho',
  'wh_material:stock_edit': 'Sửa lịch sử nhập vật tư',
  'wh_material:stock_delete': 'Xoá lịch sử nhập vật tư',
  'btp:view': 'Xem bán thành phẩm',
  'btp:item_manage': 'Quản lý danh mục bán thành phẩm',
  'btp:stock_in': 'Nhập bán thành phẩm từ NCC',
  'btp:produce': 'Tạo lệnh sản xuất bán thành phẩm',
  'btp:adjust': 'Điều chỉnh tồn bán thành phẩm',
  'btp:issue_manual': 'Xuất kho tay bán thành phẩm',
  // Từ 20260922_0001, 2 quyền này gác cả đường XUẤT ở phiên cắt lẫn đường xuất
  // thành phẩm cho đơn — nhãn cũ chỉ nói "phiếu kiểm kê" nên gây hiểu nhầm.
  // Giữ nguyên TÊN quyền (đổi tên kéo theo migration + backfill, không đáng).
  'btp:checklist_confirm': 'Xuất bán thành phẩm cho sản xuất',
  'btp:checklist_cancel': 'Hoàn bán thành phẩm về kho',
  'btp:prebuilt_manage': 'Lập phiếu sản xuất thành phẩm làm sẵn',
  'purchase:view': 'Xem đơn nhập hàng',
  'purchase:create': 'Tạo đơn nhập hàng',
  'purchase:edit': 'Sửa đơn nhập hàng',
  'purchase:delete': 'Xoá đơn nhập hàng',
  'purchase:receive': 'Xác nhận nhận hàng',
  'purchase:stock': 'Nhập kho',
  'supplier:view': 'Xem nhà cung cấp',
  'supplier:create': 'Thêm nhà cung cấp',
  'supplier:edit': 'Sửa nhà cung cấp',
  'supplier:delete': 'Xoá nhà cung cấp',
  'user:manage': 'Quản lý user & phân quyền',
  'user:create': 'Tạo tài khoản nhân viên',
  'user:reset_password': 'Đặt lại mật khẩu nhân viên',
  'user:deactivate': 'Vô hiệu hoá / kích hoạt nhân viên',
  'user:delete': 'Xoá nhân viên',
  'role:manage': 'Quản lý vai trò & ma trận quyền',
  'frame_color:manage': 'Quản lý màu khung',
  'tag:manage': 'Quản lý thẻ đơn hàng',
  'source:manage': 'Quản lý nguồn đơn hàng',
  'system:config': 'Cấu hình hệ thống (shop info)',
  'department:manage': 'Quản lý phòng ban',
  'position:manage': 'Quản lý vị trí công việc',
  'branch:manage': 'Quản lý chi nhánh',
  'shipping:config_api_key': 'Cấu hình API vận chuyển',
  'shipping:manual_status': 'Cập nhật trạng thái giao Ship ngoài',
  'report:dashboard': 'Xem dashboard báo cáo (KPI tổng)',
  'report:revenue': 'Xem báo cáo doanh thu chi tiết',
  'report:profit': 'Xem báo cáo lợi nhuận và giá vốn',
  'report:by_user': 'Xem báo cáo theo nhân viên / phòng ban',
  'report:export': 'Xuất báo cáo',
  'pancake:view': 'Xem Hiệu suất Sale',
  'pancake:manage': 'Quản lý cấu hình Pancake (Page/giờ nghỉ/token)',
  'expense:request': 'Tạo đề nghị thanh toán',
  'expense:view': 'Xem phiếu duyệt chi',
  'expense:approve': 'Duyệt / từ chối phiếu chi',
  'expense:fund_warehouse': 'Xem & chi phiếu nguồn TK cá nhân Thủ kho',
  'expense:fund_shipping': 'Xem & chi phiếu nguồn TK cá nhân Vận đơn',
  'expense:fund_treasurer': 'Xem & chi phiếu nguồn TK cá nhân Thủ quỹ',
  'expense:fund_company': 'Xem & chi phiếu nguồn TK công ty',
  'expense:fund_cash': 'Xem & chi phiếu nguồn Tiền mặt',
  'expense:flow_manage': 'Cấu hình luồng duyệt',
  'expense:type_manage': 'Quản lý loại thanh toán',
  'expense:report_view': 'Xem báo cáo chi',
  'expense:report_export': 'Xuất báo cáo chi',
  'expense:export': 'Xuất Excel danh sách phiếu chi',
  'expense:create_command': 'Tạo lệnh chi',
  'expense:transfer_tag': 'Gắn / gỡ thẻ Đã chuyển tiền',
  'deposit:create': 'Tạo phiếu đặt cọc',
  'deposit:view': 'Xem phiếu đặt cọc',
  'deposit:view:own': 'Xem phiếu đặt cọc của mình',
  'deposit:view:subordinates': 'Xem phiếu đặt cọc của nhân viên',
  'deposit:view:branch': 'Xem phiếu đặt cọc theo chi nhánh',
  'deposit:view:all': 'Xem tất cả phiếu đặt cọc',
  'deposit:confirm': 'Xác nhận đã về tiền',
  'cod_reduction:request': 'Tạo đề nghị hạ COD',
  'cod_reduction:view': 'Xem đề nghị hạ COD',
  'cod_reduction:view:own': 'Xem đề nghị hạ COD của mình',
  'cod_reduction:view:subordinates': 'Xem đề nghị hạ COD của cấp dưới',
  'cod_reduction:view:branch': 'Xem đề nghị hạ COD trong chi nhánh',
  'cod_reduction:view:all': 'Xem mọi đề nghị hạ COD',
  'cod_reduction:approve': 'Duyệt đề nghị hạ COD (cấp quản lý)',
  'cod_reduction:flow_manage': 'Cấu hình luồng duyệt hạ COD',
  'cod_reduction:reason_manage': 'Quản lý lý do hạ COD',
  'cod_reduction:report_view': 'Xem báo cáo hạ COD',
  'cod_reduction:report_export': 'Xuất Excel hạ COD',
  'lead:view': 'Xem khách tiềm năng',
  'lead:view:own': 'Xem lead của mình',
  'lead:view:subordinates': 'Xem lead của cấp dưới',
  'lead:view:branch': 'Xem lead trong chi nhánh',
  'lead:view:all': 'Xem mọi lead',
  'lead:view_idle': 'Xem kho lead nhàn rỗi (đã thu hồi)',
  'lead:create': 'Thêm lead',
  'lead:edit': 'Sửa thông tin lead',
  'lead:edit:own': 'Sửa lead của mình',
  'lead:edit:subordinates': 'Sửa lead của cấp dưới',
  'lead:edit:branch': 'Sửa lead trong chi nhánh',
  'lead:edit:all': 'Sửa mọi lead',
  'lead:delete': 'Xoá lead',
  'lead:assign': 'Phân bổ người phụ trách',
  'lead:status': 'Đổi trạng thái lead',
  'lead:deal_status_edit': 'Đổi trạng thái lead đã chốt deal',
  'lead:note': 'Ghi nhận điểm chạm (ghi chú)',
  'lead:export': 'Xuất danh sách lead',
  'lead:import': 'Nhập lead từ file',
  'lead:convert': 'Chuyển lead thành khách hàng',
  'lead_setting:manage': 'Cấu hình thu hồi lead nhàn rỗi',
  'customer_source:manage': 'Quản lý nguồn khách hàng',
  'lead_customer_type:manage': 'Quản lý loại khách hàng của lead',
  'kpi:view': 'Xem chỉ tiêu KPI',
  'kpi:view:own': 'Xem KPI của mình',
  'kpi:view:subordinates': 'Xem KPI của cấp dưới',
  'kpi:view:branch': 'Xem KPI trong chi nhánh',
  'kpi:view:all': 'Xem KPI toàn hệ thống',
  'kpi:manage': 'Đặt chỉ tiêu KPI',
  'kpi:manage:own': 'Đặt KPI cho mình',
  'kpi:manage:subordinates': 'Đặt KPI cho cấp dưới',
  'kpi:manage:branch': 'Đặt KPI trong chi nhánh',
  'kpi:manage:all': 'Đặt KPI toàn hệ thống',
  'report:lead_user': 'Báo cáo lead theo nhân viên',
  'report:kpi_user': 'Báo cáo KPI theo nhân viên',
  'report:kpi_department': 'Báo cáo KPI theo phòng ban',
  'report:production': 'Báo cáo sản lượng xưởng (m², số tranh, số đơn)',
  // Nhãn PHẢI nói rõ "có doanh thu": báo cáo này có cột tiền nên người cấp
  // quyền phải biết mình đang mở quyền xem tiền — khác report:production
  // (không có đồng nào nên mới tách được khỏi quyền tiền).
  'report:product_size': 'Báo cáo top kích thước bán chạy theo sản phẩm / theo nguồn (có doanh thu)',
  'report:slow_production': 'Báo cáo đơn sản xuất chậm (đúng hạn / trễ theo hạn của đơn)',
  'piecework:declare': 'Khai việc khoán (thợ) — khai số bức đã làm, báo sai, xem tiền của mình',
  'piecework:approve': 'Duyệt khoán — duyệt / từ chối, khai hộ, chuyển số, điều chỉnh',
  'piecework:approve:subordinates': 'Duyệt khoán của cấp dưới',
  'piecework:approve:branch': 'Duyệt khoán trong chi nhánh',
  'piecework:approve:all': 'Duyệt khoán của mọi thợ',
  'piecework:config': 'Cấu hình khoán — công đoạn, đơn giá, bảng gán, loại định mức, ngày bắt đầu',
  'piecework:close_period': 'Chốt kỳ khoán (khoá số liệu của tháng)',
  // Nhãn PHẢI nói rõ có tiền công: người cấp quyền phải biết mình mở quyền xem tiền lương.
  'report:piecework': 'Báo cáo khoán (có tiền công của thợ)',
}


